import React from "react";
import {
  Checkbox,
  CheckboxGroup,
  Column,
  Columns,
  Heading,
  List,
  ListItem,
  Stack,
  Text,
  TextLink,
} from "@origin-digital/ods-core";
import {
  Field,
  FieldInputProps,
  FieldMetaProps,
  useFormikContext,
} from "formik";
import { FormValues } from "../types";
import { InputValueType } from "@origin-digital/ods-types/.";

const HomeUserAccountBlockContent = () => {
  const { setFieldValue } = useFormikContext<FormValues>();
  return (
    <Columns
      collapseBelow="md"
      space={{
        xs: "none",
        md: "xlarge",
      }}
      alignY="stretch"
    >
      <Column width="2/3">
        <Stack
          space={{
            xs: "xlarge",
            md: "xxlarge",
          }}
        >
          {/* Privacy */}
          <Stack space="large">
            <Heading variant="h4">Your privacy</Heading>
            <Stack space="medium">
              <Text>
                You consent to Origin's collection, use, holding and disclosure
                of your personal and credit related information in accordance
                with our privacy and credit reporting statements, available at{" "}
                <TextLink href="https://originenergy.com.au/privacy">
                  originenergy.com.au/privacy
                </TextLink>
                . Our credit reporting statement explains who we disclose credit
                information to (including service providers overseas) and how
                this could affect your creditworthiness, as well as how you can
                access, correct or complain about it. Please contact us to
                request a paper copy.
              </Text>
            </Stack>
          </Stack>
          {/* End Privacy */}

          {/* Account holder agreement */}
          <Stack space="large">
            <Heading variant="h4">Account holder agreement</Heading>
            <Stack space="medium">
              <Text>
                I nominate Origin as the energy retailer for electricity and/or
                natural gas at my property. I understand that:
              </Text>
              <List>
                <ListItem>
                  Once my account's been set up Origin will supply energy on a
                  standing agreement and send through all the details, including
                  prices.
                </ListItem>
                <ListItem>
                  Clear and safe access must be available at the property. If
                  work cannot be completed due to access restrictions or other
                  issues, an additional fee could apply.
                </ListItem>
                <ListItem>
                  Origin may communicate with my builder and their associated
                  contractors in relation to the progress of my account, until
                  my connection is complete and, if applicable, the meter is
                  installed.
                </ListItem>
                <ListItem>
                  A connection fee may apply, as set out below. In NSW and QLD,
                  this fee will be included on my first bill from Origin.
                </ListItem>
              </List>
            </Stack>
          </Stack>
          {/* End Account holder agreement */}

          {/* New connection fees */}
          <Stack space="large">
            <Heading variant="h4">New connection fees</Heading>
            <Stack space="medium">
              <Text>
                For electricity in Victoria - The fee ranges between $290 to
                $4600 (incl GST) depending on your distribution area and
                requirements. Please note these fees are charged by the
                distributor and passed on by us. These fees are current as at 1
                July 2025 and do change from time to time. You can find more
                details on these fees{" "}
                <TextLink href="https://originenergy.com.au/meterfees">
                  originenergy.com.au/meterfees
                </TextLink>
                .
              </Text>
              <Text>
                For natural gas – If a fee applies, this will be quoted to us by
                your distributor and we'll let you know before the work goes
                ahead.
              </Text>
              <Text>
                Other offers – Origin has a number of other offers that may be
                of interest to you. If you'd like to take up one of these offers
                once your account's been set up, you can compare our plans at{" "}
                <TextLink href="https://originenergy.com.au/plans">
                  originenergy.com.au/plans
                </TextLink>
                .
              </Text>
            </Stack>
          </Stack>
          {/* End New connection fees */}

          {/* Energy charges */}
          <Stack space="large">
            <Heading variant="h4">Energy charges</Heading>
            <Stack space="medium">
              <Text>
                You can find our energy charges at{" "}
                <TextLink href="https://originenergy.com.au/pricing">
                  originenergy.com.au/pricing
                </TextLink>
                . The charges that will apply to you will depend on your
                metering set up and distributor and won't be confirmed until
                after your connection is complete. We will set you up on the
                most common rate type for your area and let you know if this is
                not the rate type that applies to you once your connection is
                complete.
              </Text>
              <Text>
                For premises in Victoria – Energy Fact Sheets containing all the
                key details about the offer are available upon request or at{" "}
                <TextLink href="https://originenergy.com.au/pricing">
                  originenergy.com.au/pricing
                </TextLink>{" "}
                for generally available plans.
              </Text>
              <Text>
                For electricity in ACT, NSW, QLD & SA – The reference price is a
                rate set by the regulator for electricity in each area. Energy
                companies compare prices for electricity in an area against the
                reference price. This Standing Agreement is 0% off the reference
                price. The amounts shown below are the estimated annual cost of
                this Standing Agreement for the most common tariff type for each
                network area, based on an estimated annual usage. Note that
                bills vary with usage and meter type.
              </Text>
              <Text>
                A Basic Plan Information Document containing all key details
                about a plan is available at{" "}
                <TextLink href="https://originenergy.com.au/pricing">
                  originenergy.com.au/pricing
                </TextLink>{" "}
                or upon request.
              </Text>
              {/* NSW Ausgrid */}
              <Text weight="medium">For NSW Ausgrid:</Text>
              <List space="xsmall">
                <ListItem>
                  a household using 3,900 kWh/year on a time of use tariff,
                  estimated annual cost $1,965.
                </ListItem>
                <ListItem>
                  a business using 10,000 kWh/year on a flat rate tariff,
                  estimated annual cost $4,977.
                </ListItem>
              </List>
              {/* End NSW Ausgrid */}
              {/* NSW Endeavour */}
              <Text weight="medium">For NSW Endeavour:</Text>
              <List space="xsmall">
                <ListItem>
                  a household using 4,900 kWh/year on a flat rate tariff,
                  estimated annual cost $2,411.
                </ListItem>
                <ListItem>
                  a business using 10,000 kWh/year on a flat rate tariff,
                  estimated annual cost $4,775.
                </ListItem>
              </List>
              {/* End NSW Endeavour */}
              {/* NSW Essential */}
              <Text weight="medium">For NSW Essential:</Text>
              <List space="xsmall">
                <ListItem>
                  a household using 4,600 kWh/year on a time of use tariff,
                  estimated annual cost $2,741.
                </ListItem>
                <ListItem>
                  a business using 10,000 kWh/year on a flat rate tariff,
                  estimated annual cost $6,222.
                </ListItem>
              </List>
              {/* End NSW Essential */}
              {/* QLD Energex */}
              <Text weight="medium">For QLD Energex:</Text>
              <List space="xsmall">
                <ListItem>
                  a household using 4,600 kWh/year on a flat rate tariff,
                  estimated annual cost $2,143.
                </ListItem>
                <ListItem>
                  a business using 10,000 kWh/year on a flat rate tariff,
                  estimated annual cost $4,294.
                </ListItem>
              </List>
              {/* End QLD Energex */}
              {/* SA SAPN */}
              <Text weight="medium">For SA SAPN:</Text>
              <List space="xsmall">
                <ListItem>
                  a household using 4,000 kWh/year on a flat rate tariff,
                  estimated annual cost $2,301.
                </ListItem>
                <ListItem>
                  a business using 10,000 kWh/year on a flat rate tariff,
                  estimated annual cost $5,541.
                </ListItem>
              </List>
              {/* End SA SAPN */}
            </Stack>
          </Stack>
          {/* End Energy charges */}
          <Field type="checkbox" name="homeUserAgreeTerms">
            {({
              field,
              meta,
            }: {
              field: FieldInputProps<InputValueType>;
              meta: FieldMetaProps<FormValues>;
            }) => (
              <CheckboxGroup
                id="homeUserAgreeTermsGroup"
                error={!!meta.error}
                helperText={meta.error}
              >
                <Checkbox
                  {...field}
                  label="I agree to the terms and conditions."
                  id="homeUserAgreeTerms"
                  checked={field.checked}
                  name="homeUserAgreeTerms"
                  onChange={(e) => {
                    setFieldValue("homeUserAgreeTerms", e.target.checked);
                  }}
                />
              </CheckboxGroup>
            )}
          </Field>
        </Stack>
      </Column>
      {/* HelperText */}
      <Column width="1/3" />
      {/* End HelperText */}
    </Columns>
  );
};

export default HomeUserAccountBlockContent;
